import {
  Plugins,
  type ExpressionPath,
  withSystem,
  TypeImplementation,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {TUnion, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  matchJson,
  get as getFromJson,
  type Json,
} from '@packages/utilities/json';
import {identity} from '@packages/utilities/fp/utils';
import React from 'react';
import {reportBug} from '@packages/utilities/reporter';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';

const {getIn} = ContextualExpressions;

const LightboxMediaTypes = {
  Image: 'Image',
  Video: 'Video',
} as const;

const LightboxMediaExprPaths: {
  [key: string]: ExpressionPath;
} = {
  [LightboxMediaTypes.Image]: [
    {in: 'Record', at: 'image'},
    {in: 'Record', at: 'origFileName'},
  ],
};

const type = withSystem(
  {defaultValue: {tag: LightboxMediaTypes.Image}, label: 'Type'},
  TUnion({
    [LightboxMediaTypes.Image]: TTypeConstructor(
      qualify(Plugins.name('Lightbox'), typeName('LightboxImageAndCaption'))
    ),
    [LightboxMediaTypes.Video]: TTypeConstructor(
      qualify(Plugins.name('Embed'), typeName('Video'))
    ),
  })
);

const constantNull = (_: Json) => null;
const getStringFromJson = matchJson({
  Null: constantNull,
  Boolean: constantNull,
  Number: constantNull,
  String: identity,
  Array: constantNull,
  Object: constantNull,
});
const thumbnailPath = ['embed', 'meta', 'thumb', 'url'];
const titlePath = ['embed', 'meta', 'thumb', 'url'];
const urlPath = ['embed', 'meta', 'thumb', 'url'];
const getUrlAndLabelFromJson = (
  json: Json
): {
  url: string | null;
  label: string | null;
} => {
  const url = getFromJson(json, thumbnailPath);
  const label = getFromJson(json, titlePath) || getFromJson(json, urlPath);

  return {
    url: url ? getStringFromJson(url) : null,
    label: label ? getStringFromJson(label) : null,
  };
};

const imageURLPath: ExpressionPath = [
  {in: 'Record', at: 'image'},
  {in: 'Record', at: 'url'},
];
const getLabelAndUrlFromUnion = ContextualExpressions.matchExpression({
  Union: ({tag, value}) => {
    if (tag === LightboxMediaTypes.Image) {
      // @ts-expect-error - TS2345 - Argument of type 'ExpressionPath | undefined' is not assignable to parameter of type 'ExpressionPath'.
      const imageLabelExpr = getIn(value, LightboxMediaExprPaths.Image);
      const urlExpr = getIn(value, imageURLPath);

      if (!imageLabelExpr || !urlExpr) {
        reportBug(
          'WFDL:LightboxMigrationError',
          'Lightbox migration malformed data: Expected an image file name',
          {
            team: 'core',
            priority: 'error',
            ticket: 'https://webflow.atlassian.net/browse/PLAT-764',
          }
        );
        return null;
      }
      return {
        label: ContextualExpressions.getText(stripAnn(imageLabelExpr)),
        url: ContextualExpressions.getText(stripAnn(urlExpr)),
      };
    }
    if (tag === LightboxMediaTypes.Video) {
      const videoJson = ContextualExpressions.getLiteral(
        stripAnn(value)
      )?.value;
      return videoJson
        ? getUrlAndLabelFromJson(videoJson)
        : {label: 'video-placeholder', url: ''};
    }
  },
  _: constantNull,
});

const LabelContainerDiv = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
});
const ThumbnailImg = emotionStyled('img')({
  marginRight: tokens.space[2],
  width: '20px',
  maxHeight: '20px',
});
const LabelContainer = ({url, label}: {url: string | null; label: string}) => {
  return (
    <LabelContainerDiv>
      {url ? <ThumbnailImg src={url} alt={label} /> : null}
      <span>{label}</span>
    </LabelContainerDiv>
  );
};

export const LightboxMediaType = TypeImplementation.Alias({
  type,
  renderSummary: ({expr}: {expr: ContextualExpression}) => {
    const summary = getLabelAndUrlFromUnion(stripAnn(expr));
    return summary ? (
      <LabelContainer label={summary.label || ''} url={summary.url || ''} />
    ) : (
      <span> No media data found </span>
    );
  },
  render: function LightboxMediaRender({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => void;
  }) {
    return <ExpressionEditor expression={expr} dispatch={dispatch} />;
  },
});
